import React, { useState, useEffect, useRef, useCallback, Component, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { HarmonyLogoCompact } from '../components/HarmonyLogo';
import { CandorProvider, useCandor } from '../context/CandorContext';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ScanEye, ListChecks, BookOpen, ChevronDown, LogOut, AlertTriangle, RefreshCw, ShieldCheck, SlidersHorizontal,
} from 'lucide-react';
import usePageMeta from '../hooks/usePageMeta';

const CandorSessions = lazy(() => import('../components/candor/CandorSessions'));
const CandorSessionDetail = lazy(() => import('../components/candor/CandorSessionDetail'));
const CandorGuide = lazy(() => import('../components/candor/CandorGuide'));
const CandorSignatures = lazy(() => import('../components/candor/CandorSignatures'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

class CandorErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Harmony Candor crashed:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="text-center max-w-md space-y-3">
            <AlertTriangle className="w-10 h-10 text-destructive mx-auto" />
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <Button onClick={() => window.location.reload()}>Reload</Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const TABS = [
  { key: 'interviews', label: 'Interviews', icon: ListChecks },
  { key: 'guide',      label: 'Guide',      icon: BookOpen },
  { key: 'signatures', label: 'Signatures', icon: SlidersHorizontal },
];

const CandorContent = () => {
  usePageMeta('Harmony Candor | Harmony Apps', '/favicon-candor.svg');

  const [searchParams, setSearchParams] = useSearchParams();
  const { api, userName, role } = useCandor();
  const [access, setAccess] = useState(null); // null | 'ok' | 'forbidden'
  // Signatures editing is super_admin-only (it is baked into every candidate
  // script org-wide); the backend enforces this too.
  const canEditSignatures = role === 'super_admin';
  const visibleTabs = TABS.filter((t) => t.key !== 'signatures' || canEditSignatures);
  // Lets the Signatures editor block a tab switch when it has unsaved edits.
  const signaturesDirtyRef = useRef(false);
  const setSignaturesDirty = useCallback((d) => { signaturesDirtyRef.current = d; }, []);

  useEffect(() => {
    let cancelled = false;
    api.me()
      .then(() => { if (!cancelled) setAccess('ok'); })
      .catch((e) => {
        // Only a 403 means "no access"; anything else, let the tabs load and
        // show their own errors rather than hiding the app.
        if (!cancelled) setAccess(e?.response?.status === 403 ? 'forbidden' : 'ok');
      });
    return () => { cancelled = true; };
  }, [api]);

  const tab = visibleTabs.some((t) => t.key === searchParams.get('tab')) ? searchParams.get('tab') : 'interviews';
  const sessionId = searchParams.get('session');

  const changeTab = (next) => {
    if (tab === 'signatures' && next !== 'signatures' && signaturesDirtyRef.current) {
      if (!window.confirm('Discard unsaved changes to the detection lists?')) return;
      signaturesDirtyRef.current = false;
    }
    setParams({ tab: next, session: null });
  };

  const setParams = (next) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => (v ? params.set(k, v) : params.delete(k)));
    setSearchParams(params, { replace: false });
  };

  const getInitials = (name) =>
    (name || 'U').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  if (access === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
          <p className="text-sm">Loading Harmony Candor…</p>
        </div>
      </div>
    );
  }

  if (access === 'forbidden') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center max-w-md space-y-3">
          <ShieldCheck className="w-10 h-10 text-muted-foreground mx-auto" />
          <h1 className="text-xl font-semibold">No access</h1>
          <p className="text-sm text-muted-foreground">
            You don't have access to Harmony Candor. Ask an administrator to grant it from the Harmony dashboard.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="candor-app">
      <nav className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <HarmonyLogoCompact size={32} />
              <div>
                <h1 className="font-semibold text-lg text-foreground flex items-center gap-2">
                  <ScanEye className="w-5 h-5 text-indigo-600" />
                  Harmony Candor
                </h1>
                <p className="text-xs text-muted-foreground">Harmony Apps</p>
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="candor-user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-indigo-600 text-white text-sm">
                      {getInitials(userName)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium">{userName}</span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>
                  <p className="text-sm font-medium">{userName}</p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {(role || '').replace('_', ' ')}
                  </p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => window.close()} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Close App
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="pb-2 -mx-4 px-4 overflow-x-auto">
            <Tabs value={tab} onValueChange={changeTab}>
              <TabsList>
                {visibleTabs.map((t) => {
                  const Icon = t.icon;
                  return (
                    <TabsTrigger key={t.key} value={t.key} data-testid={`candor-tab-${t.key}`}>
                      <Icon className="w-4 h-4 mr-2" />
                      {t.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Suspense
          fallback={
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              Loading…
            </div>
          }
        >
          {tab === 'interviews' && (sessionId ? (
            <CandorSessionDetail
              key={sessionId}
              sessionId={sessionId}
              onBack={() => setParams({ session: null })}
            />
          ) : (
            <CandorSessions onOpen={(id) => setParams({ session: id })} />
          ))}
          {tab === 'guide' && <CandorGuide />}
          {tab === 'signatures' && canEditSignatures && <CandorSignatures onDirtyChange={setSignaturesDirty} />}
        </Suspense>
      </main>
    </div>
  );
};

// Best-effort JWT payload decode — fallback only, used when /auth/me errors.
const _peekJwt = (jwt) => {
  try {
    const [, payload] = (jwt || '').split('.');
    if (!payload) return null;
    const base = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base + '='.repeat((4 - base.length % 4) % 4)));
  } catch (_) { return null; }
};

const CandorApp = () => {
  // Cookie bootstrap, same as Pulse: no token in the URL. The HttpOnly
  // refresh cookie from the shell login is traded for an in-memory token.
  const [bootState, setBootState] = useState({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const refresh = await axios.post(
          `${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true },
        );
        const token = refresh.data?.token;
        if (!token) throw new Error('No token in refresh response');

        let userName = 'User';
        let role = 'viewer';
        try {
          const me = await axios.get(`${BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          userName = me.data?.full_name || me.data?.username || userName;
          role = me.data?.role || role;
        } catch (_) {
          const claims = _peekJwt(token);
          if (claims?.username) userName = claims.username;
          if (claims?.role) role = claims.role;
        }

        if (!cancelled) setBootState({ status: 'ready', token, userName, role });
      } catch (e) {
        if (!cancelled) setBootState({ status: 'unauthenticated' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (bootState.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
          <p className="text-sm">Signing in…</p>
        </div>
      </div>
    );
  }

  if (bootState.status === 'unauthenticated') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-destructive mb-2">Session expired</h1>
          <p className="text-muted-foreground mb-4">
            Please sign in to the Harmony Apps dashboard, then launch Harmony Candor again.
          </p>
          <Button onClick={() => { window.location.href = `${window.location.origin}/login`; }}>
            Go to sign-in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <CandorErrorBoundary>
      <CandorProvider token={bootState.token} role={bootState.role} userName={bootState.userName}>
        <CandorContent />
      </CandorProvider>
    </CandorErrorBoundary>
  );
};

export default CandorApp;
